import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Filter, X, ChevronDown, Check } from 'lucide-react'
import { useFolders } from '../../hooks/useFolders'
import { useTags } from '../../hooks/useTags'
import { useDocumentTypes, useDepartments } from '../../hooks/useOrganization'

export interface DocumentFilters {
  folder_id?: number | null
  tags?: number[]
  document_type_id?: number | null
  department_id?: number | null
  status?: string | null
}

const STATUS_OPTIONS = [
  'draft', 'pending', 'partially_signed', 'completed', 'declined', 'recalled', 'expired',
] as const

interface FilterBarProps {
  actorId?: string
  orgKoblinkId?: string
  filters: DocumentFilters
  onFiltersChange: (filters: DocumentFilters) => void
}

type ActiveDropdown = 'folder' | 'tags' | 'type' | 'department' | 'status' | null

export function FilterBar({ actorId, orgKoblinkId, filters, onFiltersChange }: FilterBarProps) {
  const { t } = useTranslation()
  const { data: folders } = useFolders(actorId)
  const { data: tags } = useTags(actorId)
  const { data: docTypes } = useDocumentTypes(orgKoblinkId)
  const { data: departments } = useDepartments(orgKoblinkId)

  const [activeDropdown, setActiveDropdown] = useState<ActiveDropdown>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!activeDropdown) return
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setActiveDropdown(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [activeDropdown])

  const activeFilterCount = [
    filters.folder_id != null,
    (filters.tags?.length ?? 0) > 0,
    filters.document_type_id != null,
    filters.department_id != null,
    filters.status != null,
  ].filter(Boolean).length

  const clearAll = () => {
    onFiltersChange({})
    setActiveDropdown(null)
  }

  const selectedFolder = folders?.find((f) => f.id === filters.folder_id)
  const selectedType = docTypes?.find((dt) => dt.id === filters.document_type_id)
  const selectedDept = departments?.find((d) => d.id === filters.department_id)

  return (
    <div ref={containerRef} className="mb-2">
      <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-thin">
        {/* Filter icon */}
        <div className="flex items-center gap-0.5 shrink-0 text-neutral-400 dark:text-neutral-500 mr-0.5">
          <Filter size={10} />
          {activeFilterCount > 0 && (
            <span className="text-[9px] font-medium text-primary-600 dark:text-primary-400">
              {activeFilterCount}
            </span>
          )}
        </div>

        {/* Folder filter */}
        <FilterChip
          label={selectedFolder?.name ?? t('organize.filterByFolder')}
          isActive={filters.folder_id != null}
          color={selectedFolder?.color}
          isOpen={activeDropdown === 'folder'}
          onClick={() => setActiveDropdown(activeDropdown === 'folder' ? null : 'folder')}
          onClear={() => onFiltersChange({ ...filters, folder_id: undefined })}
        />

        {/* Tags filter */}
        <FilterChip
          label={
            (filters.tags?.length ?? 0) > 0
              ? `${filters.tags!.length} ${t('organize.tags').toLowerCase()}`
              : t('organize.filterByTags')
          }
          isActive={(filters.tags?.length ?? 0) > 0}
          isOpen={activeDropdown === 'tags'}
          onClick={() => setActiveDropdown(activeDropdown === 'tags' ? null : 'tags')}
          onClear={() => onFiltersChange({ ...filters, tags: undefined })}
        />

        {/* Status filter */}
        <FilterChip
          label={filters.status ? t(`status.${filters.status}`) : t('organize.filterByStatus')}
          isActive={filters.status != null}
          isOpen={activeDropdown === 'status'}
          onClick={() => setActiveDropdown(activeDropdown === 'status' ? null : 'status')}
          onClear={() => onFiltersChange({ ...filters, status: undefined })}
        />

        {/* Org-only filters */}
        {orgKoblinkId && (
          <>
            <FilterChip
              label={selectedType?.name ?? t('organize.filterByType')}
              isActive={filters.document_type_id != null}
              color={selectedType?.color}
              isOpen={activeDropdown === 'type'}
              onClick={() => setActiveDropdown(activeDropdown === 'type' ? null : 'type')}
              onClear={() => onFiltersChange({ ...filters, document_type_id: undefined })}
            />
            <FilterChip
              label={selectedDept?.name ?? t('organize.filterByDepartment')}
              isActive={filters.department_id != null}
              color={selectedDept?.color}
              isOpen={activeDropdown === 'department'}
              onClick={() => setActiveDropdown(activeDropdown === 'department' ? null : 'department')}
              onClear={() => onFiltersChange({ ...filters, department_id: undefined })}
            />
          </>
        )}

        {/* Clear all */}
        {activeFilterCount > 0 && (
          <button
            onClick={clearAll}
            className="shrink-0 text-[9px] text-neutral-400 hover:text-error-500 transition-colors px-1"
          >
            {t('organize.clearFilters')}
          </button>
        )}
      </div>

      {/* Dropdown panels */}
      {activeDropdown === 'folder' && folders && (
        <DropdownPanel>
          <DropdownOption
            label={t('organize.allFolders')}
            isSelected={filters.folder_id == null}
            onClick={() => {
              onFiltersChange({ ...filters, folder_id: undefined })
              setActiveDropdown(null)
            }}
          />
          {folders.map((f) => (
            <DropdownOption
              key={f.id}
              label={f.name}
              color={f.color}
              badge={f.document_count}
              isSelected={f.id === filters.folder_id}
              depth={f.breadcrumbs.length}
              onClick={() => {
                onFiltersChange({ ...filters, folder_id: f.id })
                setActiveDropdown(null)
              }}
            />
          ))}
        </DropdownPanel>
      )}

      {activeDropdown === 'tags' && tags && (
        <DropdownPanel>
          {tags.map((tag) => {
            const isSelected = filters.tags?.includes(tag.id) ?? false
            return (
              <DropdownOption
                key={tag.id}
                label={tag.name}
                color={tag.color}
                badge={tag.document_count}
                isSelected={isSelected}
                onClick={() => {
                  const current = filters.tags ?? []
                  const next = isSelected
                    ? current.filter((id) => id !== tag.id)
                    : [...current, tag.id]
                  onFiltersChange({ ...filters, tags: next.length > 0 ? next : undefined })
                }}
              />
            )
          })}
        </DropdownPanel>
      )}

      {activeDropdown === 'status' && (
        <DropdownPanel>
          {STATUS_OPTIONS.map((s) => (
            <DropdownOption
              key={s}
              label={t(`status.${s}`)}
              isSelected={filters.status === s}
              onClick={() => {
                onFiltersChange({ ...filters, status: filters.status === s ? undefined : s })
                setActiveDropdown(null)
              }}
            />
          ))}
        </DropdownPanel>
      )}

      {activeDropdown === 'type' && docTypes && (
        <DropdownPanel>
          <DropdownOption
            label={t('metadata.noneSelected')}
            isSelected={filters.document_type_id == null}
            onClick={() => {
              onFiltersChange({ ...filters, document_type_id: undefined })
              setActiveDropdown(null)
            }}
          />
          {docTypes.map((dt) => (
            <DropdownOption
              key={dt.id}
              label={dt.name}
              color={dt.color}
              isSelected={dt.id === filters.document_type_id}
              onClick={() => {
                onFiltersChange({ ...filters, document_type_id: dt.id })
                setActiveDropdown(null)
              }}
            />
          ))}
        </DropdownPanel>
      )}

      {activeDropdown === 'department' && departments && (
        <DropdownPanel>
          <DropdownOption
            label={t('metadata.noneSelected')}
            isSelected={filters.department_id == null}
            onClick={() => {
              onFiltersChange({ ...filters, department_id: undefined })
              setActiveDropdown(null)
            }}
          />
          {departments.map((d) => (
            <DropdownOption
              key={d.id}
              label={d.name}
              color={d.color}
              isSelected={d.id === filters.department_id}
              onClick={() => {
                onFiltersChange({ ...filters, department_id: d.id })
                setActiveDropdown(null)
              }}
            />
          ))}
        </DropdownPanel>
      )}
    </div>
  )
}

/* ----- Sub-components ----- */

function FilterChip({
  label,
  isActive,
  color,
  isOpen,
  onClick,
  onClear,
}: {
  label: string
  isActive: boolean
  color?: string
  isOpen: boolean
  onClick: () => void
  onClear: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 inline-flex items-center gap-0.5 py-0.5 px-1.5 rounded-full text-[10px] border transition-colors ${
        isActive
          ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
          : isOpen
            ? 'border-neutral-300 dark:border-neutral-600 bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300'
            : 'border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:border-neutral-300'
      }`}
    >
      {color && (
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
      )}
      <span className="truncate max-w-[60px]">{label}</span>
      {isActive ? (
        <X
          size={8}
          className="shrink-0 opacity-60 hover:opacity-100"
          onClick={(e) => {
            e.stopPropagation()
            onClear()
          }}
        />
      ) : (
        <ChevronDown size={8} className="shrink-0 opacity-40" />
      )}
    </button>
  )
}

function DropdownPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-1 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg z-50 py-1 max-h-[160px] overflow-y-auto">
      {children}
    </div>
  )
}

function DropdownOption({
  label,
  color,
  badge,
  isSelected,
  depth,
  onClick,
}: {
  label: string
  color?: string
  badge?: number
  isSelected: boolean
  depth?: number
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-1.5 px-2 py-1 text-[10px] text-left hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
      style={depth ? { paddingLeft: `${8 + depth * 10}px` } : undefined}
    >
      {color && (
        <span className="shrink-0 w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
      )}
      <span className="flex-1 truncate text-neutral-900 dark:text-neutral-100">{label}</span>
      {badge != null && <span className="text-[9px] text-neutral-400">{badge}</span>}
      {isSelected && <Check size={10} className="shrink-0 text-primary-500" />}
    </button>
  )
}
